/**
 * View manager — /settings/extensions/$moduleKey/$entityKey/views
 *
 * Lists saved views on an entity and lets the user create new ones.
 * In v1 the focus is Kanban views (the only new renderer besides
 * list that's fully implemented). Other view types are selectable
 * in the type picker but call out that the renderer is not yet
 * implemented so users know what they're getting.
 */

import type { KanbanViewConfig, ViewType } from '@cloud/modules'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { VIEW_TYPES } from '@/components/custom/views/registry'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/extensions/$moduleKey/$entityKey/views')({
  component: ViewsManagerPage,
})

function ViewsManagerPage() {
  const { moduleKey, entityKey } = Route.useParams()
  const queryClient = useQueryClient()

  const { data: views, isLoading } = useQuery(
    orpc.extensions.views.list.queryOptions({
      input: { moduleKey, entityKey },
    }),
  )

  // Load entity definition so we can populate the group-by field
  // picker. Only works for custom entities — this page is scoped to
  // custom modules in v1. Built-in entity views land when we expose
  // a "get entity manifest" endpoint.
  const { data: entityList } = useQuery(
    orpc.modules.custom.entities.list.queryOptions({ input: { moduleKey } }),
  )
  const entity = entityList?.find((e) => e.key === entityKey)
  const fields = (entity?.fields as Array<{ key: string; label: string; type: string }>) ?? []

  const [createOpen, setCreateOpen] = useState(false)

  const deleteMutation = useMutation(
    orpc.extensions.views.delete.mutationOptions({
      onSuccess: () => {
        toast.success('View deleted')
        void queryClient.invalidateQueries({
          queryKey: orpc.extensions.views.list.queryKey({
            input: { moduleKey, entityKey },
          }),
        })
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl space-y-4">
      <Link
        to="/settings"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to settings
      </Link>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>
              Views on{' '}
              <code className="font-mono text-base">
                {moduleKey}.{entityKey}
              </code>
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Saved renderings of this entity — list, Kanban, calendar (coming soon), etc.
            </p>
          </div>
          <Button type="button" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New view
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : !views || views.length === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center py-6">
              No saved views yet. The default list view is always available.
            </p>
          ) : (
            <ul className="divide-y">
              {views.map((view) => (
                <li key={view.viewKey} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{view.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {view.type}
                      </Badge>
                      {view.isDefault && (
                        <Badge variant="outline" className="text-xs">
                          default
                        </Badge>
                      )}
                    </div>
                    {view.description && (
                      <p className="text-xs text-muted-foreground mt-1">{view.description}</p>
                    )}
                    <p className="text-xs font-mono text-muted-foreground mt-0.5">
                      {view.viewKey}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (confirm(`Delete view "${view.name}"?`)) {
                        deleteMutation.mutate({
                          moduleKey,
                          entityKey,
                          viewKey: view.viewKey,
                        })
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <CreateViewDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        moduleKey={moduleKey}
        entityKey={entityKey}
        fields={fields}
      />
    </div>
  )
}

// ---------- Create dialog ----------

function CreateViewDialog({
  open,
  onClose,
  moduleKey,
  entityKey,
  fields,
}: {
  open: boolean
  onClose: () => void
  moduleKey: string
  entityKey: string
  fields: Array<{ key: string; label: string; type: string }>
}) {
  const queryClient = useQueryClient()
  const [viewKey, setViewKey] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<ViewType>('kanban')
  const [groupByField, setGroupByField] = useState('')
  const [titleField, setTitleField] = useState('')

  const createMutation = useMutation(
    orpc.extensions.views.create.mutationOptions({
      onSuccess: () => {
        toast.success(`View "${name}" created`)
        void queryClient.invalidateQueries({
          queryKey: orpc.extensions.views.list.queryKey({
            input: { moduleKey, entityKey },
          }),
        })
        onClose()
        setViewKey('')
        setName('')
        setDescription('')
        setType('kanban')
        setGroupByField('')
        setTitleField('')
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  // Build the type-specific config from the form fields
  const buildConfig = (): Record<string, unknown> => {
    if (type === 'kanban') {
      const cfg: KanbanViewConfig = {
        groupByField,
        titleField: titleField || undefined,
      }
      return cfg as unknown as Record<string, unknown>
    }
    // List + other types: empty config for now. Users can refine
    // later via a dedicated edit page (future polish).
    return {}
  }

  const handleSave = () => {
    if (!viewKey || !name) {
      toast.error('Key and name are required')
      return
    }
    if (type === 'kanban' && !groupByField) {
      toast.error('Kanban views need a group-by field')
      return
    }
    createMutation.mutate({
      moduleKey,
      entityKey,
      viewKey,
      type,
      name,
      description: description || undefined,
      config: buildConfig(),
    })
  }

  const selectableFields = fields.filter(
    (f) => f.type === 'select' || f.type === 'multi_select',
  )

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New view</DialogTitle>
          <DialogDescription>
            Create a saved view on{' '}
            <code className="font-mono text-xs">
              {moduleKey}.{entityKey}
            </code>
            .
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="view-key">Key</Label>
              <Input
                id="view-key"
                value={viewKey}
                onChange={(e) => setViewKey(e.target.value)}
                placeholder="invoices_by_status"
                pattern="[a-z][a-z0-9_]*"
              />
            </div>
            <div>
              <Label htmlFor="view-name">Name</Label>
              <Input
                id="view-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="By status"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="view-desc">Description</Label>
            <Textarea
              id="view-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div>
            <Label htmlFor="view-type">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as ViewType)}>
              <SelectTrigger id="view-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VIEW_TYPES.map((t) => (
                  <SelectItem key={t.key} value={t.key} disabled={!t.implemented}>
                    {t.name}
                    {!t.implemented && (
                      <span className="text-xs text-muted-foreground ml-2">(coming soon)</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {type === 'kanban' && (
            <>
              <div>
                <Label htmlFor="kb-group">Group by field *</Label>
                <Select value={groupByField} onValueChange={setGroupByField}>
                  <SelectTrigger id="kb-group">
                    <SelectValue placeholder="Pick a select/multi_select field" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectableFields.length === 0 ? (
                      <SelectItem value="__none__" disabled>
                        No select fields on this entity
                      </SelectItem>
                    ) : (
                      selectableFields.map((f) => (
                        <SelectItem key={f.key} value={f.key}>
                          {f.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Kanban columns are the values of this field.
                </p>
              </div>
              <div>
                <Label htmlFor="kb-title">Card title field</Label>
                <Select value={titleField || '__none__'} onValueChange={(v) => setTitleField(v === '__none__' ? '' : v)}>
                  <SelectTrigger id="kb-title">
                    <SelectValue placeholder="— auto —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— auto (first text field) —</SelectItem>
                    {fields.map((f) => (
                      <SelectItem key={f.key} value={f.key}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating…' : 'Create view'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
